import React from 'react';
import { 
  Users, 
  Target, 
  Heart, 
  Sparkles, 
  Brain, 
  MessageCircle,
  ArrowRight,
  BarChart,
  Rocket 
} from 'lucide-react';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: Users,
    title: 'Nossa Missão',
    description: 'Capacitar criadores com ferramentas inteligentes para alcançar seu potencial máximo'
  },
  {
    icon: Target,
    title: 'Nossa Visão',
    description: 'Ser a principal plataforma de estratégia de conteúdo impulsionada por IA'
  },
  {
    icon: Heart,
    title: 'Nossos Valores',
    description: 'Inovação, transparência e foco no sucesso do cliente'
  }
];

const differentials = [
  {
    icon: Brain,
    title: 'IA Avançada',
    description: 'Algoritmos inteligentes que personalizam estratégias para cada perfil'
  },
  {
    icon: BarChart,
    title: 'Resultados Reais',
    description: 'Métricas claras e resultados mensuráveis para seu crescimento'
  },
  {
    icon: Sparkles,
    title: 'Interface Intuitiva',
    description: 'Ferramentas poderosas com usabilidade simplificada'
  },
  {
    icon: MessageCircle,
    title: 'Suporte Dedicado',
    description: 'Atendimento personalizado para maximizar seus resultados'
  }
];

export default function AboutPage() {
  return (
    <div className="relative min-h-screen">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center text-center px-4">
        <div className="relative max-w-4xl mx-auto">
          {/* Logo Icon */}
          <div className="inline-flex items-center justify-center w-16 h-16 mb-8 rounded-2xl bg-white/10 backdrop-blur-sm">
            <Rocket className="h-8 w-8 text-white" />
          </div>

          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Sobre a Kontty
          </h1>
          
          <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto">
            A Kontty é uma plataforma inovadora que combina inteligência artificial e tecnologia 
            avançada para transformar a maneira como criadores de conteúdo e pequenas empresas 
            planejam suas estratégias.
          </p>
        </div>
      </div>

      {/* Values Section */}
      <div className="relative py-24">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} className="p-8 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 hover:bg-white/15 transition-all">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 text-center">{value.title}</h3>
                  <p className="text-white/80 text-center">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Journey Section */}
      <div className="relative py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Nossa Jornada
            </h2>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <p className="text-white/90 leading-relaxed mb-6">
                Fundada com o propósito de democratizar estratégias de conteúdo, a Kontty surgiu da 
                paixão por ajudar pequenos criadores e empresas a crescerem em um mercado cada vez mais competitivo. 
                Inspirada na tecnologia de IA, nossa plataforma oferece soluções práticas e acessíveis para 
                maximizar o impacto digital.
              </p>
              <div className="flex items-center justify-center">
                <Rocket className="h-12 w-12 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Differentials Section */}
      <div className="relative py-24">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-12 text-center">
            Nossos Diferenciais
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {differentials.map((differential, index) => {
              const Icon = differential.icon;
              return (
                <div 
                  key={index}
                  className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all"
                >
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {differential.title}
                  </h3>
                  <p className="text-white/80">
                    {differential.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative py-24">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6">
              Pronto para Transformar sua Presença Online?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Junte-se a milhares de criadores que já estão revolucionando sua estratégia de conteúdo.
            </p>
            <Link
              to="/cadastro"
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-primary-600 bg-white rounded-full hover:bg-primary-50 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Comece Agora
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}